import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Modal,
  ModalDialog,
  Option,
  Select,
  Stack,
  Textarea,
  Tooltip,
  Typography,
} from '@mui/joy';
import CodeMirror from '@uiw/react-codemirror';
import { python } from '@codemirror/lang-python';
import { autocompletion } from '@codemirror/autocomplete';
import HelpIcon from '@mui/icons-material/Help';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';
import validatePythonCode from '../../../components/functions/validatePythonCode';
import debounce from 'lodash/debounce';

// Custom autocomplete for function context objects
const functionContextCompletions = (context) => {
  const word = context.matchBefore(/[\w.]*/);
  if (!word || (word.from === word.to && !context.explicit)) return null;

  const text = word.text;

  // Define all context objects and their properties (high priority - boost 50)
  const contextObjects = {
    result: {
      info: 'Task result instance with output data',
      type: 'variable',
      boost: 50,
      properties: {
        output: { info: 'Dictionary containing task output data', type: 'property' },
        task: { info: 'Reference to the associated task', type: 'property' },
      },
    },
    task: {
      info: 'Task instance with payload and status',
      type: 'variable',
      boost: 50,
      properties: {
        id: { info: 'Unique identifier of the task', type: 'property' },
        status: { info: 'Current status of the task', type: 'property' },
        payload: { info: 'Dictionary containing task input data', type: 'property' },
        events: { info: 'List of task events', type: 'property' },
      },
    },
    requests: {
      info: 'HTTP request utilities for API calls',
      type: 'variable',
      boost: 50,
      properties: {
        get: { info: 'requests.get(url, **kwargs) - Send GET request', type: 'method' },
        post: { info: 'requests.post(url, data=None, json=None) - Send POST request', type: 'method' },
        put: { info: 'requests.put(url, data=None) - Send PUT request', type: 'method' },
        delete: { info: 'requests.delete(url) - Send DELETE request', type: 'method' },
        patch: { info: 'requests.patch(url, data=None) - Send PATCH request', type: 'method' },
        head: { info: 'requests.head(url) - Send HEAD request', type: 'method' },
      },
    },
    datetime: {
      info: 'Date and time utilities',
      type: 'variable',
      boost: 50,
      properties: {
        now: { info: 'datetime.now() - Current local datetime', type: 'method' },
        today: { info: 'datetime.today() - Current local date', type: 'method' },
        utcnow: { info: 'datetime.utcnow() - Current UTC datetime', type: 'method' },
        strptime: { info: 'datetime.strptime(string, format) - Parse string to datetime', type: 'method' },
        strftime: { info: 'datetime.strftime(format) - Format to string', type: 'method' },
        isoformat: { info: 'datetime.isoformat() - Return ISO 8601 string', type: 'method' },
      },
    },
    timedelta: {
      info: 'timedelta(days=0, hours=0, minutes=0, seconds=0) - Time duration',
      type: 'class',
      boost: 45,
      properties: {},
    },
    output: {
      info: 'Set this variable to return data from the function',
      type: 'variable',
      boost: 50,
      properties: {},
    },
    error: {
      info: 'Set this variable to trigger an error in function execution',
      type: 'variable',
      boost: 50,
      properties: {},
    },
  };

  // Common Python built-ins (medium priority - boost 30)
  const pythonBuiltins = [
    { label: 'print', type: 'function', info: 'print(*args) - Print to console (captured in logs)', boost: 40 },
    { label: 'len', type: 'function', info: 'len(obj) - Return length of object', boost: 30 },
    { label: 'str', type: 'function', info: 'str(obj) - Convert to string', boost: 30 },
    { label: 'int', type: 'function', info: 'int(x) - Convert to integer', boost: 30 },
    { label: 'float', type: 'function', info: 'float(x) - Convert to float', boost: 30 },
    { label: 'list', type: 'function', info: 'list(iterable) - Create list', boost: 30 },
    { label: 'dict', type: 'function', info: 'dict(**kwargs) - Create dictionary', boost: 30 },
    { label: 'json', type: 'variable', info: 'JSON encoding/decoding module', boost: 35 },
    { label: 'True', type: 'constant', info: 'Boolean true value', boost: 25 },
    { label: 'False', type: 'constant', info: 'Boolean false value', boost: 25 },
    { label: 'None', type: 'constant', info: 'None value', boost: 25 },
    { label: 'range', type: 'function', info: 'range(stop) or range(start, stop, step)', boost: 25 },
    { label: 'enumerate', type: 'function', info: 'enumerate(iterable) - Return index, value pairs', boost: 25 },
    { label: 'isinstance', type: 'function', info: 'isinstance(obj, classinfo) - Check type', boost: 25 },
    { label: 'Exception', type: 'class', info: 'Base exception class', boost: 20 },
  ];

  // Python keywords (low priority - boost 10)
  const pythonKeywords = [
    { label: 'if', type: 'keyword', info: 'Conditional statement', boost: 10 },
    { label: 'elif', type: 'keyword', info: 'Else-if statement', boost: 10 },
    { label: 'else', type: 'keyword', info: 'Else statement', boost: 10 },
    { label: 'for', type: 'keyword', info: 'For loop', boost: 10 },
    { label: 'while', type: 'keyword', info: 'While loop', boost: 10 },
    { label: 'try', type: 'keyword', info: 'Try block', boost: 10 },
    { label: 'except', type: 'keyword', info: 'Exception handler', boost: 10 },
    { label: 'finally', type: 'keyword', info: 'Finally block', boost: 10 },
    { label: 'return', type: 'keyword', info: 'Return statement', boost: 10 },
    { label: 'def', type: 'keyword', info: 'Define function', boost: 10 },
    { label: 'class', type: 'keyword', info: 'Define class', boost: 10 },
    { label: 'import', type: 'keyword', info: 'Import module', boost: 10 },
    { label: 'from', type: 'keyword', info: 'Import from module', boost: 10 },
    { label: 'as', type: 'keyword', info: 'Alias', boost: 10 },
    { label: 'in', type: 'keyword', info: 'Membership test', boost: 10 },
    { label: 'not', type: 'keyword', info: 'Boolean not', boost: 10 },
    { label: 'and', type: 'keyword', info: 'Boolean and', boost: 10 },
    { label: 'or', type: 'keyword', info: 'Boolean or', boost: 10 },
    { label: 'is', type: 'keyword', info: 'Identity test', boost: 10 },
    { label: 'with', type: 'keyword', info: 'Context manager', boost: 10 },
    { label: 'raise', type: 'keyword', info: 'Raise exception', boost: 10 },
    { label: 'pass', type: 'keyword', info: 'Null operation', boost: 10 },
    { label: 'break', type: 'keyword', info: 'Break loop', boost: 10 },
    { label: 'continue', type: 'keyword', info: 'Continue loop', boost: 10 },
  ];

  // JSON module completions
  const jsonProperties = {
    dumps: { info: 'json.dumps(obj) - Serialize to JSON string', type: 'method' },
    loads: { info: 'json.loads(s) - Parse JSON string', type: 'method' },
    dump: { info: 'json.dump(obj, fp) - Serialize to file', type: 'method' },
    load: { info: 'json.load(fp) - Parse from file', type: 'method' },
  };

  // Check if we're completing a property (e.g., "result.")
  const dotIndex = text.lastIndexOf('.');
  if (dotIndex !== -1) {
    const objName = text.substring(0, dotIndex);
    const propPrefix = text.substring(dotIndex + 1).toLowerCase();

    // Check for json.* completions
    if (objName === 'json') {
      const options = Object.entries(jsonProperties)
        .filter(([name]) => name.toLowerCase().startsWith(propPrefix))
        .map(([name, prop]) => ({
          label: name,
          type: prop.type,
          info: prop.info,
          apply: name,
          boost: 40,
        }));

      if (options.length > 0) {
        return {
          from: word.from + dotIndex + 1,
          options,
          validFor: /^[\w]*$/,
        };
      }
    }

    // Check for context object properties
    const obj = contextObjects[objName];
    if (obj && obj.properties) {
      const options = Object.entries(obj.properties)
        .filter(([name]) => name.toLowerCase().startsWith(propPrefix))
        .map(([name, prop]) => ({
          label: name,
          type: prop.type,
          info: prop.info,
          apply: name,
          boost: 40,
        }));

      if (options.length > 0) {
        return {
          from: word.from + dotIndex + 1,
          options,
          validFor: /^[\w]*$/,
        };
      }
    }
    return null;
  }

  // Top-level completions - combine all sources
  const lowerText = text.toLowerCase();

  // Context objects (highest priority)
  const contextOptions = Object.entries(contextObjects)
    .filter(([name]) => name.toLowerCase().startsWith(lowerText))
    .map(([name, obj]) => ({
      label: name,
      type: obj.type,
      info: obj.info,
      boost: obj.boost,
    }));

  // Python builtins
  const builtinOptions = pythonBuiltins
    .filter(item => item.label.toLowerCase().startsWith(lowerText))
    .map(item => ({ ...item }));

  // Python keywords
  const keywordOptions = pythonKeywords
    .filter(item => item.label.toLowerCase().startsWith(lowerText))
    .map(item => ({ ...item }));

  const allOptions = [...contextOptions, ...builtinOptions, ...keywordOptions];

  if (allOptions.length === 0) return null;

  return {
    from: word.from,
    options: allOptions,
    validFor: /^[\w]*$/,
  };
};

// Create the autocomplete extension with context completions as additional source
const functionAutocompletion = autocompletion({
  override: [functionContextCompletions],
  defaultKeymap: true,
  icons: true,
  activateOnTyping: true,
  optionClass: () => 'cm-function-completion',
});

const DESCRIPTION_MAX_LENGTH = 300;

const FunctionEditorModal = ({
  open,
  onClose,
  onSubmit,
  onOpenHelp,
  initialData,
  isEdit,
  validateName,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    code: '',
    function_type: '',
    interval: null,
  });
  const [initialFormData, setInitialFormData] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const editorRef = useRef(null);

  // Initialize form data when modal opens or initialData changes
  useEffect(() => {
    if (open) {
      const data = initialData || {
        name: '',
        description: '',
        code: '',
        function_type: '',
        interval: null,
      };
      setFormData(data);
      setInitialFormData(data);
      setErrors({});
      setIsFullscreen(false);
    }
  }, [open, initialData]);

  // Debounced code update
  const debouncedSetCode = useMemo(
    () => debounce((value) => {
      setFormData(prev => ({ ...prev, code: value }));
      setErrors(prev => ({ ...prev, code: '' }));
    }, 200),
    []
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => debouncedSetCode.cancel();
  }, [debouncedSetCode]);

  // Check if form has changes
  const hasChanges = useMemo(() => {
    if (!initialFormData) return true;
    return JSON.stringify(formData) !== JSON.stringify(initialFormData);
  }, [formData, initialFormData]);

  // Handle field changes
  const handleChange = (field) => (e) => {
    const value = e?.target?.value ?? e;
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  // Handle type change (reset interval if not scheduled)
  const handleTypeChange = (_, newValue) => {
    setFormData(prev => ({
      ...prev,
      function_type: newValue || '',
      interval: newValue === 'scheduled' ? prev.interval : null,
    }));
    setErrors(prev => ({ ...prev, function_type: '' }));
  };

  // Handle interval change
  const handleIntervalChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      interval: value === '' ? null : parseInt(value, 10),
    }));
    setErrors(prev => ({ ...prev, interval: '' }));
  };

  // Revert to initial data
  const handleRevert = () => {
    if (initialFormData) {
      setFormData(initialFormData);
      setErrors({});
    }
  };

  // Validate form
  const validate = useCallback(() => {
    const newErrors = {};

    // Name validation
    if (!formData.name?.trim()) {
      newErrors.name = 'Function name is required';
    } else {
      const nameError = validateName?.(formData.name, isEdit, initialData?.id);
      if (nameError) {
        newErrors.name = nameError;
      }
    }

    // Type validation
    if (!formData.function_type) {
      newErrors.function_type = 'Function type is required';
    }

    // Interval validation for scheduled functions
    if (formData.function_type === 'scheduled') {
      if (formData.interval === null || formData.interval === undefined) {
        newErrors.interval = 'Interval is required for scheduled functions';
      } else if (formData.interval < 10) {
        newErrors.interval = 'Interval must be at least 10 seconds';
      }
    }

    // Code validation
    const codeValidation = validatePythonCode(formData.code || '');
    if (!codeValidation.isValid) {
      newErrors.code = codeValidation.error;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, validateName, isEdit, initialData?.id]);

  // Handle submit
  const handleSubmit = useCallback(async (e) => {
    e?.preventDefault();

    if (!validate()) {
      setIsFullscreen(false);
      return;
    }

    try {
      setIsSubmitting(true);
      const success = await onSubmit(formData);
      if (success) {
        onClose();
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [validate, onSubmit, formData, onClose]);

  // Keyboard shortcut for save
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!open) return;
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        if (hasChanges || !isEdit) {
          handleSubmit();
        }
      }
      if (e.key === 'Escape' && !isFullscreen) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, hasChanges, isEdit, isFullscreen, handleSubmit, onClose]);

  // Format interval for display
  const formatIntervalPreview = (seconds) => {
    if (!seconds || isNaN(seconds) || seconds < 10) return null;
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    const parts = [];
    if (days > 0) parts.push(`${days} day${days === 1 ? '' : 's'}`);
    if (hours > 0) parts.push(`${hours} hour${hours === 1 ? '' : 's'}`);
    if (minutes > 0) parts.push(`${minutes} minute${minutes === 1 ? '' : 's'}`);
    if (secs > 0 || parts.length === 0) parts.push(`${secs} second${secs === 1 ? '' : 's'}`);
    return parts.join(', ');
  };

  const intervalPreview = formatIntervalPreview(formData.interval);

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog
        sx={{
          width: isFullscreen ? '95vw' : '80vw',
          maxWidth: isFullscreen ? 'none' : 1100,
          minHeight: isFullscreen ? '95vh' : undefined,
          maxHeight: '95vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
            <Typography level="title-lg">
              {isEdit ? 'Edit Function' : 'Create New Function'}
            </Typography>
            <Stack direction="row" spacing={0.5}>
              <Tooltip title="Documentation">
                <IconButton variant="plain" color="neutral" onClick={onOpenHelp}>
                  <HelpIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
                <IconButton variant="plain" color="neutral" onClick={() => setIsFullscreen(!isFullscreen)}>
                  {isFullscreen ? <CloseFullscreenIcon /> : <OpenInFullIcon />}
                </IconButton>
              </Tooltip>
              <IconButton variant="plain" color="neutral" onClick={onClose}>
                <CloseIcon />
              </IconButton>
            </Stack>
          </Stack>
        </DialogTitle>

        {/* Content */}
        <DialogContent sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {!isFullscreen && (
            <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
              {isEdit ? 'Update the function details and code.' : 'Configure your function and write Python code.'}
            </Typography>
          )}

          <Stack
            direction={isFullscreen ? 'column' : 'row'}
            spacing={2}
            sx={{ flex: 1, minHeight: 0 }}
          >
            {/* Form Section */}
            {!isFullscreen && (
              <Stack spacing={2} sx={{ width: '28%', minWidth: 240, flexShrink: 0 }}>
                <FormControl required error={!!errors.name}>
                  <FormLabel>Function Name</FormLabel>
                  <Input
                    placeholder="Enter function name"
                    value={formData.name}
                    onChange={handleChange('name')}
                  />
                  {errors.name && (
                    <Typography level="body-xs" color="danger" sx={{ mt: 0.5 }}>
                      {errors.name}
                    </Typography>
                  )}
                </FormControl>

                <FormControl>
                  <FormLabel>Description</FormLabel>
                  <Textarea
                    placeholder="Describe what this function does"
                    value={formData.description}
                    onChange={(e) => {
                      const value = e.target.value.slice(0, DESCRIPTION_MAX_LENGTH);
                      setFormData(prev => ({ ...prev, description: value }));
                    }}
                    minRows={3}
                    maxRows={5}
                  />
                  <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
                    <Typography
                      level="body-xs"
                      color={formData.description.length >= DESCRIPTION_MAX_LENGTH ? 'danger' : 'neutral'}
                    >
                      {formData.description.length}/{DESCRIPTION_MAX_LENGTH}
                    </Typography>
                  </Stack>
                </FormControl>

                <FormControl required error={!!errors.function_type}>
                  <FormLabel>Function Type</FormLabel>
                  <Select
                    value={formData.function_type}
                    onChange={handleTypeChange}
                    placeholder="Select type"
                  >
                    <Option value="post-complete">Post-Complete</Option>
                    <Option value="scheduled">Scheduled</Option>
                  </Select>
                  {errors.function_type && (
                    <Typography level="body-xs" color="danger" sx={{ mt: 0.5 }}>
                      {errors.function_type}
                    </Typography>
                  )}
                </FormControl>

                <FormControl
                  required={formData.function_type === 'scheduled'}
                  error={!!errors.interval}
                  disabled={formData.function_type !== 'scheduled'}
                >
                  <FormLabel>Interval (seconds)</FormLabel>
                  <Input
                    type="number"
                    placeholder="e.g., 300"
                    value={formData.interval ?? ''}
                    onChange={handleIntervalChange}
                    slotProps={{ input: { min: 10 } }}
                  />
                  {intervalPreview && (
                    <Typography level="body-xs" sx={{ mt: 0.5, fontStyle: 'italic', color: 'text.tertiary' }}>
                      {intervalPreview}
                    </Typography>
                  )}
                  {errors.interval && (
                    <Typography level="body-xs" color="danger" sx={{ mt: 0.5 }}>
                      {errors.interval}
                    </Typography>
                  )}
                </FormControl>
              </Stack>
            )}

            {/* Code Editor Section */}
            <Stack spacing={1} sx={{ flex: 1, minWidth: 0, minHeight: 0 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <FormLabel sx={{ m: 0 }}>Python Code *</FormLabel>
                  <Tooltip
                    title={
                      <Box sx={{ p: 1 }}>
                        <Typography level="body-sm" sx={{ mb: 1 }}>Available context objects:</Typography>
                        <Typography level="body-xs">• result - Task result data</Typography>
                        <Typography level="body-xs">• task - Task instance</Typography>
                        <Typography level="body-xs">• requests - HTTP utilities</Typography>
                        <Typography level="body-xs">• datetime - Date utilities</Typography>
                        <Typography level="body-xs">• output - Set return value</Typography>
                        <Typography level="body-xs">• error - Set error message</Typography>
                      </Box>
                    }
                    placement="right"
                  >
                    <IconButton size="sm" variant="plain">
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
                <Stack direction="row" spacing={0.5}>
                  {isEdit && (
                    <Tooltip title="Revert changes">
                      <IconButton
                        size="sm"
                        variant="plain"
                        color="warning"
                        onClick={handleRevert}
                        disabled={!hasChanges}
                      >
                        <RestartAltIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {isFullscreen && (
                    <Tooltip title="Documentation">
                      <IconButton size="sm" variant="plain" onClick={onOpenHelp}>
                        <HelpIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              </Stack>

              <Card
                variant="outlined"
                sx={{
                  flex: 1,
                  minHeight: isFullscreen ? '70vh' : 380,
                  borderColor: errors.code ? 'danger.500' : 'neutral.outlinedBorder',
                  overflow: 'hidden',
                  p: 0,
                }}
              >
                <CodeMirror
                  value={formData.code}
                  onChange={debouncedSetCode}
                  height="100%"
                  style={{ height: '100%' }}
                  extensions={[python(), functionAutocompletion]}
                  basicSetup={{
                    lineNumbers: true,
                    autocompletion: false, // Disable default, use our custom one
                  }}
                  onCreateEditor={(view) => {
                    editorRef.current = view;
                  }}
                />
              </Card>
              {errors.code && (
                <Typography level="body-xs" color="danger">
                  {errors.code}
                </Typography>
              )}
            </Stack>
          </Stack>
        </DialogContent>

        {/* Footer */}
        <DialogActions sx={{ pt: 2 }}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%', justifyContent: 'flex-end' }}>
            <Typography level="body-xs" color="neutral" sx={{ fontStyle: 'italic' }}>
              {isEdit ? (hasChanges ? '⌘S to save' : 'No changes') : '⌘S to create'}
            </Typography>
            <Button variant="plain" color="neutral" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="solid"
              color="primary"
              onClick={handleSubmit}
              disabled={(isEdit && !hasChanges) || isSubmitting}
              loading={isSubmitting}
              sx={{ minWidth: 100 }}
            >
              {isEdit ? 'Save' : 'Create'}
            </Button>
          </Stack>
        </DialogActions>
      </ModalDialog>
    </Modal>
  );
};

export default FunctionEditorModal;
